// @givmo/org-console — ACTIVITY: the append-only record of every access change.
//
// ⚠ THE NOUN MATTERS AND IT IS NOT THE OBVIOUS ONE. A class-specific console calls this exact surface
// by a word this package may not say on an organization-facing screen — the copy rail bans that root
// outright, comments included. The backend already made the substitution: the route is
// `access/events` and the fields are `event_type` / `occurred_at` / `payload`. So this is the ACCESS
// EVENT LOG, rendered as "Activity", and a port-copy that carried the old noun in would trip the rail
// on the way in. That is the rail working, not the rail being awkward.
//
// ⚠ GLOBAL-ADMIN ONLY, AND THE GATE IS THE STRICT ONE. The read is refused for a plain Member, so
// gating the FETCH on the permissive flag would spend a request the backend will not answer.
// `tierUnknown` is separated from "resolved Member" for the reason it is separated everywhere: only
// one of them is recoverable, so only one gets a retry, and only the other may state what this person
// is allowed to do.
//
// DATA CONTRACT: the `<prefix>-events-*` test-id family (a row's cells: `-row-<id>-detail`, and the
// "By" name `-row-<id>-actor`).

import { useMemo } from 'react';
import {
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  Icon,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { History } from 'lucide-react';
import { testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { labelMidSentence } from './org-address';
import { AUTOMATIC_CONNECTION_EVENT_COPY } from './org-automatic-connection-copy';
import { formatOrgEventTime, nameForOrgUserId } from './org-decode';
import { EXTERNAL_COPY } from './org-external';
import { measurementFieldLabel } from './org-measurement-model';
import { ROLE_OWNER, moduleTierLabel, tierLabel } from './org-tiers';
import { useOrgAccessEvents, useOrgAccessGate, useOrgMembers } from './use-org-console';
import {
  OrgConsoleUnavailableNotice,
  OrgNotPermittedNotice,
  OrgTierUnknownNotice,
} from './org-console-notices';
import type { OrgAccessEventRow, OrgConsoleMemberRow } from './wire-types';

/**
 * The "By" word for a row no person made (Org Machinery OM-43, RULED 2026-10-17 20:51 PDT, "fine").
 * Givmo itself writes some rows with NO actor — an operator act through the console's grant
 * writer, such as a backfill of an app's access — and a blank "By" on the organization's one
 * record of who did what reads as a person missing, not as Givmo.
 */
const EVENT_ACTOR_GIVMO = 'Givmo';

/**
 * Who made the change, for the "By" column: the person the roster names (or its word for someone
 * it no longer holds), and Givmo for a row with no actor. The actor's word ONLY: an empty SUBJECT
 * means the change was the organization's, never Givmo's, so "Who it affected" keeps its blank.
 */
function eventActorName(
  actorUserId: string | null,
  members: readonly OrgConsoleMemberRow[],
): string {
  return actorUserId === null ? EVENT_ACTOR_GIVMO : nameForOrgUserId(actorUserId, members);
}

/**
 * A sentence for an event type.
 *
 * An unrecognized type renders as ITSELF rather than as nothing — the backend's vocabulary is a
 * hand-maintained set that can gain a member before this package ships copy for it, and a dated row
 * saying `SOMETHING_NEW` beside a named actor is still evidence, while a blank one is not.
 */
const EVENT_LABELS: Record<string, string> = {
  TIER_CHANGED: 'Access level changed',
  MODULE_ACCESS_GRANTED: 'App access granted',
  MODULE_ACCESS_REVOKED: 'App access removed',
  MODULE_ROLE_CHANGED: 'App level changed',
  CUSTOM_PERMISSIONS_SET: 'Custom permissions set',
  CUSTOM_PERMISSIONS_CLEARED: 'Custom permissions cleared',
  MODULE_SETTING_CHANGED: 'App switched on or off',
  // The one type with no twin on any other console: the actor and the subject are different humans.
  CONNECTION_REVOKED: 'Connected AI ended',
  // ⚠ THE ONE TYPE THAT IS NOT AN ACCESS CHANGE. The backend appends it from the Measurement card,
  // one row per id actually changed, and it lands in this same log because this is the organization's
  // one record of what its admins did. Copy for it ships HERE rather than being left to the
  // render-as-itself fallback: that fallback exists for a type the backend gains before this package
  // hears about it, and a row reading `MEASUREMENT_CONFIG_CHANGED` in a product surface is not a
  // fallback working, it is copy nobody wrote.
  MEASUREMENT_CONFIG_CHANGED: 'Measurement ID changed',
  // Membership ends (OM-10 leg 4) — the one row whose subject is no longer on the roster.
  MEMBER_REMOVED: 'Removed from the team',
  // The access-request workflow (shell contract v3, T5.2): opened / approved / declined. The
  // approval also appends the ordinary MODULE_ACCESS_GRANTED row, through the same grant writer.
  ACCESS_REQUESTED: 'Access requested',
  ACCESS_REQUEST_APPROVED: 'Access request approved',
  ACCESS_REQUEST_DENIED: 'Access request declined',
  // The organization's account closed (OM-9; the words RULED D-CPO-74, 2026-10-24, Org Machinery
  // OM-47). The act appends ONE row for the whole organization, with no subject and no `module`, and
  // its ids and counts print nothing (`isDeactivationBookkeeping`). An organization that comes back
  // keeps every row from before its closure, so its new team reads the closure among them.
  ORG_DEACTIVATED: 'Organization deactivated',
  // An organization setting changed (Org Machinery OM-17 seam A, design 4A §11 item 7 — RULED): one
  // row per key that changed, its detail the setting's own words (`settingChangePhrase`). Like the
  // measurement row it is not an access change; it lands here because this is the organization's one
  // record of what its admins did. An org-level row carries no `module`, so it shows no app tag.
  SETTING_CHANGED: 'Setting changed',
  // The External mark (§OM-53): a label about a person, never access. Its row's `module` is null, so
  // it shows no app tag. This is the NEUTRAL headline, for a row whose payload cannot say which way
  // the mark moved; `eventLabel` reads the direction off the payload whenever it can.
  EXTERNAL_MARK_CHANGED: EXTERNAL_COPY.changed,
  // The organization's automatic connection (Givmo MCP MCP-P17b, D-MCP-106): a Global Admin turned it
  // on, changed its issuer, or turned it off. The NEUTRAL headline; `eventLabel` reads on or off off
  // the payload whenever it can, and the detail names the issuer (`splitEventDetail`).
  AUTOMATIC_CONNECTION_CHANGED: AUTOMATIC_CONNECTION_EVENT_COPY.changed,
};

/** The External mark's event type (§OM-53 §3.4). */
const EXTERNAL_MARK_CHANGED = 'EXTERNAL_MARK_CHANGED';

/** The automatic connection's event type (MCP-P17b). */
const AUTOMATIC_CONNECTION_CHANGED = 'AUTOMATIC_CONNECTION_CHANGED';

/** The issuer address one side of an automatic-connection row names, or '' when it names none. */
function automaticConnectionIssuerOf(side: unknown): string {
  if (side === null || typeof side !== 'object' || Array.isArray(side)) return '';
  const issuer = (side as Record<string, unknown>).issuer;
  return typeof issuer === 'string' ? issuer.trim() : '';
}

/** The mark's new state off the row's `to`, or `null` when the payload does not record it. */
function externalMarkTo(payload: Record<string, unknown> | null | undefined): boolean | null {
  const to = payload?.to;
  if (to === null || typeof to !== 'object' || Array.isArray(to)) return null;
  const external = (to as Record<string, unknown>).external;
  return typeof external === 'boolean' ? external : null;
}

/**
 * The page's top line (D-CPO-69 question 10, Jonathan's words; Org Machinery OM-65 (2)): the record
 * holds the organization's setting changes beside its access changes, so "who can do what" was not
 * true of it. The record's second sentence is unchanged.
 */
export const ACTIVITY_LEDE =
  'Every change to your organization’s access and settings, newest first. This record cannot be edited.';

/** Every type this panel has copy for — exported so a host's table-driven test can cover them all. */
export const KNOWN_EVENT_TYPES: readonly string[] = Object.keys(EVENT_LABELS);

/**
 * The row's headline. `payload` refines the ONE type that records two different facts:
 * `MODULE_SETTING_CHANGED` is the switch (`field: 'enabled'`, or no `field` on a row written before
 * the key existed) AND, from shell contract v3 until EP-WEB36 dropped it, the per-module
 * approval-policy setting (`field: 'access_request_approval_policy'`, T4.4) — rows the log keeps. A
 * row about the policy must not claim the app was switched — the detail column already says which
 * setting moved, and the headline agrees.
 */
export function eventLabel(eventType: string, payload?: Record<string, unknown> | null): string {
  if (eventType === 'MODULE_SETTING_CHANGED') {
    const field = typeof payload?.field === 'string' ? payload.field.trim() : '';
    if (field.length > 0 && field !== 'enabled') return 'App setting changed';
  }
  if (eventType === EXTERNAL_MARK_CHANGED) {
    // The direction, only when the payload records it as a boolean: never a guess.
    const to = externalMarkTo(payload);
    if (to !== null) return to ? EXTERNAL_COPY.marked : EXTERNAL_COPY.unmarked;
  }
  if (eventType === AUTOMATIC_CONNECTION_CHANGED) {
    // On or off, only when one side names no issuer and the other does: never a guess.
    const from = automaticConnectionIssuerOf(payload?.from);
    const to = automaticConnectionIssuerOf(payload?.to);
    if (from.length === 0 && to.length > 0) return AUTOMATIC_CONNECTION_EVENT_COPY.on;
    if (from.length > 0 && to.length === 0) return AUTOMATIC_CONNECTION_EVENT_COPY.off;
  }
  return EVENT_LABELS[eventType] ?? eventType;
}

/**
 * Per-KEY renderers for the event's untyped bag.
 *
 * ⚠ `base_role` CARRIES THE ORG-WIDE TIER AND MUST NOT REACH A PERSON RAW. A tier change is recorded
 * as `{"base_role": "Owner"} → {"base_role": "Member"}`, and the generic formatter would print
 * literally `base_role: Owner` — putting the raw wire string on the ONE screen the display rename
 * exists for, in a console where the same word simultaneously means a MODULE tier.
 *
 * ⚠ `granted_role` DELIBERATELY DOES NOT GET THE SAME TREATMENT. It is the per-MODULE tier, where
 * `Owner` is the correct word. The two keys carrying the same two strings and rendering differently
 * IS the model, and a row showing one beside the other is the record telling the truth about two
 * different facts.
 */
const VALUE_RENDERERS: Record<string, (value: unknown) => string> = {
  base_role: (value) => {
    const raw = typeof value === 'string' ? value.trim() : '';
    // An absent previous tier stays absent rather than becoming a confident "Member": the labeller's
    // least-privilege fallback is right for a LIVE row and wrong for a historical one that recorded
    // nothing.
    return raw.length === 0 ? '' : tierLabel(raw);
  },
  // A measurement row records WHICH id moved as the wire's own column name — `ga4_measurement_id`.
  // The generic formatter would print that verbatim, which is the identifier rather than the thing.
  // An unknown key still renders as itself, so a fourth id added on the backend degrades to a
  // readable row rather than to a blank one. The per-module setting rides the same `field` key.
  field: (value) => (typeof value === 'string' ? fieldLabel(value.trim()) : ''),
  // The per-module tier, in the request events' payloads.
  granted_role: (value) =>
    typeof value === 'string' && value.trim().length > 0 ? moduleTierLabel(value) : '',
  requested_role: (value) =>
    typeof value === 'string' && value.trim().length > 0 ? moduleTierLabel(value) : '',
  access_request_approval_policy: (value) =>
    typeof value === 'string' ? retiredApprovalPolicyLabel(value) : '',
};

/**
 * The words of the per-module approval-policy setting, for the rows the log KEEPS. The setting was
 * dropped on every app of both consoles (EP-WEB36, D-EP-58: a module's Owners and the Global Admins
 * always approve), so no new row names it; but the log is append-only, and a row written while it
 * existed must stay readable rather than show the wire's own words. History-only copy: nothing else
 * says these words any more.
 */
function retiredApprovalPolicyLabel(policy: string): string {
  return policy === 'global_admins_only'
    ? 'Global Admins only'
    : `${moduleTierLabel(ROLE_OWNER)}s and Global Admins`;
}

/** The setting words a `field` key may name: the measurement ids, or the retired approval policy. */
function fieldLabel(field: string): string {
  if (field === 'access_request_approval_policy') return 'Who can approve requests';
  if (field === 'enabled') return 'Switch';
  return measurementFieldLabel(field);
}

/** The words a known enum value renders as, when it appears on its own (a transition side). */
function enumWord(value: string): string | null {
  if (value === 'owners_and_global_admins' || value === 'global_admins_only') {
    return retiredApprovalPolicyLabel(value);
  }
  return null;
}

/**
 * A compact rendering of one event's payload bag.
 *
 * The column is an untyped map so one table can record every change shape, which means this reads a
 * bag it cannot type. It formats the shapes the backend actually writes (a tier, a boolean switch, a
 * permission list) and otherwise says nothing at all — an unreadable value renders as '' rather than
 * as `[object Object]`, which is worse than silence on a record people rely on.
 */
export function formatEventValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    const items = value.filter((item): item is string => typeof item === 'string');
    return items.length === 0 ? '' : items.join(', ');
  }
  if (typeof value === 'object') {
    const parts: string[] = [];
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      const renderer = VALUE_RENDERERS[key];
      const rendered = renderer === undefined ? formatEventValue(item) : renderer(item);
      if (rendered.length > 0) parts.push(`${key}: ${rendered}`);
    }
    return parts.join(' · ');
  }
  return '';
}

/**
 * The payload, split into what the record can say in ENGLISH and what is left over as wire keys.
 *
 * ⚠ THE RAW LINE WAS THE RECORD'S BODY, and the phone layout is what put it there. In the desktop
 * table `module: sales_gifting · enabled: Off` was one cramped cell among five; stacked, it sits full
 * width directly under a friendly label of the same fact, in the darkest grey in the block — a
 * snake_case identifier outranking the human sentence above it and restating it. Five review lenses
 * converged on that one line.
 *
 * The cure is not to delete it: a key the backend adds tomorrow must still be READABLE rather than
 * silently dropped, which is exactly what `formatEventValue`'s render-as-itself fallback is for. So
 * the split — keys this panel has English for go in the friendly half WITHOUT their wire name, and
 * everything else keeps the fallback, on its own line, painted lightest.
 *
 * `module` appears in NEITHER half: the event line above already names it, and printing it twice is
 * the duplication this split exists to end.
 */
export interface EventDetail {
  /** What the change was, in words. '' when the payload carries nothing this panel has copy for. */
  friendly: string;
  /** The keys with no friendly rendering, as `key: value` — the honest fallback, never the headline. */
  raw: string;
}

/**
 * Per-key sentences. A key is here only when the panel can say the whole fact — `granted_role: Member`
 * becomes "App access level: Member", not a bare "Member" a reader has to attribute.
 *
 * ⚠ `base_role` KEEPS ITS RENAME. It carries the ORG-WIDE tier, where `Owner` displays as "Global
 * Admin"; `granted_role` is the per-MODULE tier, where `Owner` is the correct word. Two keys, the
 * same two strings, deliberately different renderings — that IS the model.
 */
const FRIENDLY_KEYS: Record<string, (value: unknown) => string> = {
  base_role: (value) => {
    const rendered = VALUE_RENDERERS.base_role?.(value) ?? '';
    return rendered.length === 0 ? '' : `Access level: ${rendered}`;
  },
  granted_role: (value) =>
    typeof value === 'string' && value.trim().length > 0
      ? `App access level: ${moduleTierLabel(value)}`
      : '',
  // The request events (shell contract v3): the tier asked for, rendered as the per-module word.
  requested_role: (value) =>
    typeof value === 'string' && value.trim().length > 0
      ? `Level asked for: ${moduleTierLabel(value)}`
      : '',
  enabled: (value) => (value === true ? 'Switched on' : value === false ? 'Switched off' : ''),
  field: (value) => VALUE_RENDERERS.field?.(value) ?? '',
};

/** Keys the friendly half consumes as a PAIR rather than one at a time. */
const TRANSITION_KEYS = ['from', 'to'] as const;

/**
 * Keys that are the record's own bookkeeping, never a fact a person reads: the module (named by the
 * event line above), a request's row id (an identifier, not a thing), and a setting change's key,
 * revision and channel (the key is named by its label; the other two are the store's counters).
 */
const SILENT_KEYS: readonly string[] = ['module', 'request_id', 'key', 'revision', 'channel'];

/** A setting change's words, consumed by `settingChangePhrase` rather than printed as wire keys. */
const SETTING_CHANGE_KEYS: readonly string[] = [
  'label',
  'from_label',
  'to_label',
  'value_recorded',
];

/**
 * A deactivation's payload (OM-47) is the act's own bookkeeping, which the record does not print: the
 * ids of the people the act ended (identifiers, not things — the panel names people from the roster,
 * and the roster of an organization that comes back no longer holds them) and how many it ended of
 * each kind, in the stores' own words. The panel has no words for those counts, and a known row
 * prints no wire key, so the headline is the whole fact.
 *
 * ⚠ THE SILENCE FOLLOWS THE SHAPE, NOT A LIST OF NAMES. The act's one writer keys a count for every
 * store it ends, and that set grows with the machinery, on the backend's schedule (OM-42 adds three
 * for the organization's SSO state). A list of names here would chase it across repositories with
 * nothing to catch a miss, and each count it missed would print as a wire key on every new closure
 * row. So a number (a count) or a list of ids is silent, and any other key the act records later
 * still reads, on the raw line.
 */
function isDeactivationBookkeeping(value: unknown): boolean {
  return (
    typeof value === 'number' ||
    (Array.isArray(value) && value.every((item) => typeof item === 'string'))
  );
}

/**
 * Why a fact was CLEARED by an act rather than changed by a person — the row's `cause` (D-CPO-62:
 * the physical address leaves with the people who entered it; every fact goes at a processed
 * removal), in D-CPO-69 question 9's words. The server serves the code alone, so the words are here.
 */
const CLEAR_REASONS: Readonly<Record<string, string>> = {
  org_deactivated: 'Organization deactivated',
  claim_approved_for_another_party: 'Claimed by another party',
  removal_processed: 'Removed from Givmo',
};

/** A clear's reason in words, or `null` for a cause this panel has no words for. */
function clearReason(cause: unknown): string | null {
  return typeof cause === 'string' ? (CLEAR_REASONS[cause.trim()] ?? null) : null;
}

/** One side of a setting change: its choice label as it read then, else its value in words. */
function settingSide(value: unknown, label: unknown): string {
  if (typeof label === 'string' && label.trim().length > 0) return label.trim();
  if (value === null || value === undefined) return 'Not set';
  return formatEventValue(value);
}

/**
 * A setting change, in the words it was made in (design 4A §11 item 7, RULED): "<setting>: <old> →
 * <new>". The payload carries the setting's label and each side's choice label as they read at write
 * time, so a relabelled or retired setting still reads as it did; a cleared side reads "Not set", a
 * boolean "On"/"Off" (`formatEventValue`). A row with no label falls back to its raw key, so the
 * record stays readable.
 */
function settingChangePhrase(bag: Record<string, unknown>): string {
  const label =
    typeof bag.label === 'string' && bag.label.trim().length > 0
      ? bag.label.trim()
      : typeof bag.key === 'string'
        ? bag.key.trim()
        : '';
  // A CLEAR is an act's consequence, not a person's change (D-CPO-62), so it says it was cleared and
  // why: "Physical address cleared · Organization deactivated" (D-CPO-69 question 9). A cause this
  // panel has no words for still says "cleared", and keeps its code on the raw line below.
  if (typeof bag.cause === 'string' && bag.cause.trim().length > 0) {
    const cleared = label.length > 0 ? `${label} cleared` : 'Cleared';
    const reason = clearReason(bag.cause);
    return reason === null ? cleared : `${cleared} · ${reason}`;
  }
  // ⚠ A ROW THAT RECORDS NO VALUE SAYS ONLY THAT IT CHANGED (Design 4C §5, D-CPO-62's "Also"). An
  // address may be a person's home, so its rows carry who and when and never the address
  // (`value_recorded: false`); printing "Not set → Not set" from the absent sides would be the record
  // telling a story it did not record. The words come from the row's own label: "Changed the
  // physical address" (D-CPO-69 question 7).
  if (bag.value_recorded === false) {
    return label.length > 0 ? `Changed the ${labelMidSentence(label)}` : 'Changed';
  }
  const transition = `${settingSide(bag.from, bag.from_label)} → ${settingSide(bag.to, bag.to_label)}`;
  return label.length > 0 ? `${label}: ${transition}` : transition;
}

/**
 * ONE SIDE of a transition, in words — and the fix for the raw wire key the mockup pass caught.
 *
 * ⚠ A TIER CHANGE IS RECORDED AS `{from: {base_role: "Member"}, to: {base_role: "Owner"}}`, and the
 * generic formatter printed `base_role: Member → base_role: Global Admin` — the wire key on the one
 * screen the display rename exists for (mockup requirement 1). A side that is a bag of keys this
 * panel has words for renders the WORDS, without their keys; an unknown key inside it keeps the
 * `key: value` fallback so it stays readable; a known enum string renders as its word.
 */
function transitionSide(value: unknown): string {
  if (typeof value === 'string') return enumWord(value) ?? value;
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const parts: string[] = [];
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      const renderer = VALUE_RENDERERS[key];
      const rendered = renderer === undefined ? formatEventValue(item) : renderer(item);
      if (rendered.length === 0) continue;
      parts.push(renderer === undefined ? `${key}: ${rendered}` : rendered);
    }
    return parts.join(' · ');
  }
  return formatEventValue(value);
}

/**
 * ⚠ A TRANSITION IS RENDERED ONLY FROM WHAT THE PAYLOAD CARRIES. `from` absent is a real state — the
 * id was not set before — and it renders as "Set to X" rather than as an arrow from an invented
 * blank. Guessing the missing half would be the record telling a story the log did not record.
 */
function transitionPhrase(bag: Record<string, unknown>): string {
  const from = 'from' in bag ? transitionSide(bag.from) : '';
  const to = 'to' in bag ? transitionSide(bag.to) : '';
  if (from.length > 0 && to.length > 0) return `${from} → ${to}`;
  if (to.length > 0) return `Set to ${to}`;
  if (from.length > 0) return `Was ${from}`;
  return '';
}

export function splitEventDetail(payload: unknown, eventType?: string): EventDetail {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return { friendly: '', raw: formatEventValue(payload) };
  }
  const bag = payload as Record<string, unknown>;
  // ⚠ THE MARK'S HEADLINE ALREADY CARRIES ITS TRANSITION ("Marked as external"), so its detail omits
  // the `from`/`to` pair: "external: Off → external: On" under it would restate the headline in wire
  // keys. What is left is how it arrived: "From their invitation", or nothing.
  if (eventType === EXTERNAL_MARK_CHANGED && externalMarkTo(bag) !== null) {
    const leftover: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(bag)) {
      if (SILENT_KEYS.includes(key) || key === 'via') continue;
      if ((TRANSITION_KEYS as readonly string[]).includes(key)) continue;
      leftover[key] = value;
    }
    return {
      friendly: bag.via === 'invite' ? EXTERNAL_COPY.fromTheirInvitation : '',
      raw: formatEventValue(leftover),
    };
  }
  // The automatic connection's detail is the issuer it trusts now (or, turned off, the one it
  // trusted): the headline already says which way it moved, and the sides' wire keys would print
  // the kind and the key-set address in raw form.
  if (eventType === AUTOMATIC_CONNECTION_CHANGED) {
    const to = automaticConnectionIssuerOf(bag.to);
    return { friendly: to.length > 0 ? to : automaticConnectionIssuerOf(bag.from), raw: '' };
  }
  const settingChange = eventType === 'SETTING_CHANGED';
  const friendly: string[] = [];
  const leftover: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(bag)) {
    // Named by the event line above, or an identifier — see `SILENT_KEYS`.
    if (SILENT_KEYS.includes(key)) continue;
    if ((TRANSITION_KEYS as readonly string[]).includes(key)) continue;
    if (settingChange && SETTING_CHANGE_KEYS.includes(key)) continue;
    if (eventType === 'ORG_DEACTIVATED' && isDeactivationBookkeeping(value)) continue;
    // A clear's cause is said in the phrase when the panel has words for it; else it stays readable.
    if (settingChange && key === 'cause' && clearReason(value) !== null) continue;
    const render = FRIENDLY_KEYS[key];
    if (render === undefined) {
      leftover[key] = value;
      continue;
    }
    const rendered = render(value);
    if (rendered.length > 0) friendly.push(rendered);
  }

  const transition = settingChange ? settingChangePhrase(bag) : transitionPhrase(bag);
  if (transition.length > 0) friendly.push(transition);

  return { friendly: friendly.join(' · '), raw: formatEventValue(leftover) };
}

export interface OrgAccessEventsPanelProps {
  config: OrgConsoleConfig;
  /**
   * The host's page already carries this title, so render it for assistive technology alone —
   * the roster panel's own seam, here because the Activity page names "Activity" once (mockup
   * requirement 2). HIDDEN, NEVER REMOVED: the `<h2>` keeps its place in the heading outline.
   */
  headingHidden?: boolean;
}

export function OrgAccessEventsPanel({ config, headingHidden = false }: OrgAccessEventsPanelProps) {
  const prefix = `${testIdPrefix(config)}-events`;
  const gate = useOrgAccessGate(config.orgRef);
  // STRICT: a read the backend refuses for a Member must not be fired on a permissive guess.
  const events = useOrgAccessEvents(config.orgRef, gate.isGlobalAdminConfirmed);
  // Names come from the roster this page already reads. Best effort and never a join it blocks on.
  const roster = useOrgMembers(config.orgRef);
  const members = useMemo(() => roster.data ?? [], [roster.data]);
  const rows = useMemo(() => events.data ?? [], [events.data]);

  const columns: DataTableColumn<OrgAccessEventRow>[] = useMemo(
    () => [
      {
        id: 'change',
        header: 'Change',
        width: '34%',
        cell: (row) => (
          <div className="min-w-0">
            {/* Below `lg` the module joins the event on ONE line ("App access granted · Books"): the
                stacked record's first line is what it IS, and splitting a two-word fact across two
                lines spends the record's strongest position twice. */}
            <div className="truncate font-w600 text-text-strong max-lg:inline">
              {eventLabel(row.event_type, row.payload)}
            </div>
            {typeof row.payload?.module === 'string' && (
              <>
                <span className="hidden text-s13 text-text-tertiary max-lg:inline"> · </span>
                <div className="truncate text-s13 text-text-tertiary max-lg:inline">
                  {config.vocab.moduleLabel(row.payload.module)}
                </div>
              </>
            )}
          </div>
        ),
      },
      {
        id: 'subject',
        header: 'Who it affected',
        // ⚠ LABELLED IN THE STACK, and this is the fact the header row was carrying. Two bare names
        // in one record — whose access changed, and who changed it — are told apart on desktop by
        // the columns they sit in. Stacked, order was the only cue, and order is not a label.
        stacked: 'line' as const,
        stackedLabel: 'for',
        cell: (row) => {
          const name = nameForOrgUserId(row.subject_user_id, members);
          if (name.length === 0) return null;
          return <span className="text-text-secondary">{name}</span>;
        },
      },
      {
        id: 'detail',
        header: 'Detail',
        stacked: 'line' as const,
        cell: (row) => {
          const { friendly, raw } = splitEventDetail(row.payload, row.event_type);
          if (friendly.length === 0 && raw.length === 0) return null;
          return (
            <span
              className="text-s13 text-text-secondary"
              {...testIdProp(`${prefix}-row-${row.id}-detail`)}
            >
              {friendly}
              {/* The leftover keys, on their own line in the stack and LIGHTEST — a fallback that
                  keeps an unknown key readable must not outrank the sentence above it. */}
              {raw.length > 0 && (
                <span className="text-text-tertiary max-lg:mt-2 max-lg:block">
                  {/* A separator only where the two share a line — the stacked reading gives the
                      leftover keys their own, so a dangling middot there would be punctuation for
                      nothing. */}
                  {friendly.length > 0 && <span className="max-lg:hidden"> · </span>}
                  {raw}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'actor',
        header: 'By',
        // ⚠ PROVENANCE IS ONE LINE, SET APART. Who did it and when are the record's metadata, not two
        // more facts — so the stack composes them into a single line with space above it, and `when`
        // stands down there rather than repeating the timestamp on a line of its own. The fact is
        // not dropped: it moves into this cell for the stacked reading only.
        stacked: 'line' as const,
        stackedLabel: 'by',
        // The air that sets provenance apart from the facts belongs to the CELL: an inline label
        // followed by a block-level value would start a new line and split "by" from the name.
        stackedClassName: 'max-lg:mt-6 max-lg:text-text-tertiary',
        cell: (row) => (
          // One line at desktop width: a name or a time broken across lines reads as two facts.
          <span className="text-text-secondary lg:whitespace-nowrap max-lg:text-text-tertiary">
            <span {...testIdProp(`${prefix}-row-${row.id}-actor`)}>
              {eventActorName(row.actor_user_id, members)}
            </span>
            <span className="hidden max-lg:inline">
              {' · '}
              <span className="tabular-nums">{formatOrgEventTime(row.occurred_at)}</span>
            </span>
          </span>
        ),
      },
      {
        id: 'when',
        header: 'When',
        align: 'right' as const,
        // Carried by the provenance line above in the stacked reading.
        stacked: 'omit' as const,
        cell: (row) => (
          <span className="text-text-tertiary tabular-nums lg:whitespace-nowrap">
            {formatOrgEventTime(row.occurred_at)}
          </span>
        ),
      },
    ],
    [members, prefix, config.vocab],
  );

  if (gate.isUnavailable || (events.isError && isOrgConsoleUnavailable(events.error))) {
    return (
      <OrgConsoleUnavailableNotice
        testId={`${prefix}-unavailable`}
        title="Activity"
        description="This environment doesn’t have the console turned on, so there is no activity to show yet."
      />
    );
  }

  if (gate.isPending) {
    return (
      <Card>
        <CardBody>
          <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-loading`)}>
            Loading…
          </p>
        </CardBody>
      </Card>
    );
  }

  // Settled, but the standing read FAILED — genuinely unknown, and recoverable, so the one retry.
  if (gate.tierUnknown) {
    return <OrgTierUnknownNotice testId={`${prefix}-tier-retry`} onRetry={gate.refetch} />;
  }

  if (!gate.isGlobalAdminConfirmed) {
    return (
      <OrgNotPermittedNotice
        testId={`${prefix}-not-permitted`}
        title="Only a Global Admin can see this"
        description={`Ask a Global Admin at your ${config.vocab.orgNoun} to review changes to who can do what.`}
      />
    );
  }

  return (
    <Card>
      <CardBody className="flex flex-col gap-16">
        <div>
          <h2
            className={
              headingHidden
                ? 'sr-only'
                : 'font-sans text-s16 font-w700 tracking-givmo text-text-strong'
            }
          >
            Activity
          </h2>
          <p className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {ACTIVITY_LEDE}
          </p>
        </div>

        <DataTable
          testId={`${prefix}-list`}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTestId={() => `${prefix}-row`}
          // ⚠ FIVE COLUMNS, TWO OF THEM VISIBLE AT 390px (OM-13) — the rest behind a horizontal
          // scroll with nothing announcing it, on a record whose whole purpose is being readable.
          // Below `sm` each entry is a stacked block: the change leads, the rest reads as its detail.
          // This record has no action, so no row action is offered.
          // ⚠ `lg`, NOT `sm`, AND THIS PANEL ONLY. Five columns including a full timestamp do not fit
          // the ~720px a 768px viewport leaves once the drawer takes the sidebar's place: every
          // column wrapped, and the date fragmented to FOUR lines ("Aug 24, / 2026, / 11:41 / AM").
          // The table fits the checker and reads as a spreadsheet crushed until it went green. The
          // rule that makes this a decision rather than a dodge: the record's TABLE reading returns
          // with the DESKTOP LAYOUT — below `lg` the console is in drawer mode and this is a stacked
          // record. The roster, with four short columns, genuinely fits at 768 and stays at `sm`.
          stackAt="lg"
          loadingLabel="Loading your activity…"
          loading={events.isPending}
          error={events.isError ? 'We couldn’t load your activity.' : undefined}
          onRetry={() => void events.refetch()}
          emptyState={
            <EmptyState
              testId={`${prefix}-empty-state`}
              icon={<Icon icon={History} size={20} aria-hidden />}
              title="No changes yet"
              description="When someone changes a teammate’s access or switches an app on or off, it shows up here."
            />
          }
          footer={
            events.hasNextPage ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={events.isFetchingNextPage}
                onClick={() => void events.fetchNextPage()}
                testId={`${prefix}-load-more`}
              >
                {events.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            ) : null
          }
        />
      </CardBody>
    </Card>
  );
}
